import axios from 'axios';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { AsyncTaskType } from '../../types/async-task/async-task.type';
import { PaginationType } from '../../types/utils/pagination.type';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { additionalQueryParams, history } from '../../utils/utilities';
import { setShowModal, setModalContent } from '../utils/utils.slice';
import {
  BulkChangeAssignmentDto,
  ChangeAssignmentDto,
  CreateDIDs,
  DeleteDIDDto,
  DIDBulkRecord,
  DIDDetailsType,
  DIDE911Type,
  DIDsListItemType,
  DIDType,
  UpdateDIDDto,
} from '../../types/did/did.type';
import { AddressType } from '../../types/address/address.type';
import { getDIDListPath } from '../../routes/Router';
import { rejectWithApiError } from '../../utils/api';
import { RootReducerState } from '../../redux/Reducers';
import {
  CSRPConditionaTranslationPrefixRuleType,
  CSRPCosDestinationType,
  CSRPInformation,
} from '../../types/csrp/csrp.type';

export const postDIDs = createAsyncThunk<
  AsyncTaskType,
  {
    data: {
      pbx?: { id: number };
      pri?: { id: number };
      serviceType?: { id: number };
      telcoInstance?: { id: number };
      dids: Array<DIDBulkRecord & { country_code: string }>;
    };
  },
  { state: RootReducerState }
>('dids/postDIDs', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<AsyncTaskType>(`${process.env.REACT_APP_API_URL}/dids/bulk`, data);
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating DIDs!' });
  }
});

export const postDIDDetails = createAsyncThunk<
  { success: { did: string }[]; errors: { did: string; message: string }[] },
  { data: CreateDIDs },
  {
    state: RootReducerState;
  }
>('dids/postDIDDetails', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<{ success: { did: string }[]; errors: { did: string; message: string }[] }>(
      `${process.env.REACT_APP_API_URL}/dids`,
      data
    );
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating DID!' });
  }
});

export const getDIDsList = createAsyncThunk<
  PaginationType<DIDsListItemType>,
  RouteParamsType & {
    controller?: AbortController;
  },
  { state: RootReducerState }
>('dids/getDIDsList/no-loader', async (data, thunkAPI) => {
  try {
    const { controller, ...params } = data;
    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      thunkAPI.getState().sharedReducer.selectedLocation,
      thunkAPI.getState().sharedReducer.includeSuborgs,
      thunkAPI.getState().sharedReducer.selectedCountry
    );
    const mergedParams = { ...params, ...additionalParams };
    const queryString = Object.keys(mergedParams)
      .map(key => key + '=' + mergedParams[key])
      .join('&');

    const response = await axios.get<PaginationType<DIDsListItemType>>(
      `${process.env.REACT_APP_API_URL}/dids${queryString ? `?${queryString}` : ''}`,
      controller ? { signal: controller.signal } : undefined
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching DIDs!' });
  }
});

export const getDIDDetails = createAsyncThunk<DIDDetailsType, { id: number }, { state: RootReducerState }>(
  'dids/getDIDDetails',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.get<DIDDetailsType>(`${process.env.REACT_APP_API_URL}/dids/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching DID!' });
    }
  }
);

export const patchDIDDetails = createAsyncThunk<
  DIDDetailsType,
  { id: number; data: UpdateDIDDto; disableHistory?: boolean },
  { state: RootReducerState }
>('dids/patchDIDDetails', async ({ data, disableHistory, id }, thunkAPI) => {
  try {
    const response = await axios.patch<DIDDetailsType>(`${process.env.REACT_APP_API_URL}/dids/${id}`, data);
    setTimeout(() => {
      if (!disableHistory) {
        history.push(getDIDListPath);
      }
    });
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating DID!' });
  }
});

export const patchAssignmentDID = createAsyncThunk<
  DIDDetailsType,
  { id: number; data: ChangeAssignmentDto },
  { state: RootReducerState }
>('dids/patchAssignmentDID', async ({ data, id }, thunkAPI) => {
  try {
    const response = await axios.patch<DIDDetailsType>(
      `${process.env.REACT_APP_API_URL}/dids/${id}/change-assignment`,
      data
    );

    thunkAPI.dispatch(getDIDDetails({ id }));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating DID!' });
  }
});

export const postBulkChangeDidAssignment = createAsyncThunk<
  void,
  { data: BulkChangeAssignmentDto },
  { state: RootReducerState; rejectValue: string }
>('dids/postBulkChangeDidAssignment', async ({ data }, thunkAPI) => {
  try {
    await axios.post(`${process.env.REACT_APP_API_URL}/dids/bulk-change-assignment`, data);
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during bulk assignment change!' });
  }
});

export const patchDIDContract = createAsyncThunk<
  DIDDetailsType,
  { id: number; contractId: number | null },
  { state: RootReducerState }
>('dids/patchDIDContract', async ({ id, contractId }, thunkAPI) => {
  try {
    const payload = contractId ? { contract: { id: contractId } } : { contract: null };

    const response = await axios.patch<DIDDetailsType>(`${process.env.REACT_APP_API_URL}/dids/${id}/contract`, payload);

    thunkAPI.dispatch(getDIDDetails({ id }));
    return response.data;
  } catch (error) {
    return rejectWithApiError({
      thunkAPI,
      error,
      fallback: 'Error during updating DID contract!',
    });
  }
});

export const deleteDIDDetails = createAsyncThunk<
  DIDDetailsType,
  { id: number; fetchList: boolean; data?: DeleteDIDDto },
  { state: RootReducerState }
>('dids/deleteDIDDetails', async ({ id, fetchList, data }, thunkAPI) => {
  try {
    const response = await axios.delete<DIDDetailsType>(`${process.env.REACT_APP_API_URL}/dids/${id}`, { data });
    if (fetchList) thunkAPI.dispatch(getDIDsList(thunkAPI.getState().didsReducer.didsRouteParams));
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting DID!' });
  }
});

export const postDIDFixService = createAsyncThunk<
  { success: boolean },
  { id: number; fetchList: boolean },
  { state: RootReducerState }
>('dids/postDIDFixService', async ({ id, fetchList }, thunkAPI) => {
  try {
    const response = await axios.post<{
      success: boolean;
    }>(`${process.env.REACT_APP_API_URL}/dids/${id}/fix-service`, {});

    if (fetchList) thunkAPI.dispatch(getDIDsList(thunkAPI.getState().didsReducer.didsRouteParams));
    else thunkAPI.dispatch(getDIDDetails({ id }));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fixing DID service!' });
  }
});

/** @deprecated */
export const postDIDFixRouting = createAsyncThunk<{ success: boolean }, { id: number }, { state: RootReducerState }>(
  'dids/postDIDFixRouting',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.post<{
        success: boolean;
      }>(`${process.env.REACT_APP_API_URL}/dids/${id}/route`, {});
      thunkAPI.dispatch(getDIDDetails({ id }));
      thunkAPI.dispatch(getDIDRouting({ id }));
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fixing DID routing!' });
    }
  }
);

export const getDIDRouting = createAsyncThunk<
  { data: Record<string, unknown> },
  { id: number },
  { state: RootReducerState }
>('dids/getDIDRouting', async ({ id }, thunkAPI) => {
  try {
    const response = await axios.get<{ data: Record<string, unknown> }>(
      `${process.env.REACT_APP_API_URL}/dids/${id}/routing_status`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting DID Routing!' });
  }
});

export const getDIDAvailability = createAsyncThunk<
  { dids: Array<{ object: DIDType; did: string }>; available: boolean },
  { dids: string },
  {
    state: RootReducerState;
  }
>('dids/getDIDAvailability', async ({ dids }, thunkAPI) => {
  try {
    const response = await axios.get<{ dids: Array<{ object: DIDType; did: string }>; available: boolean }>(
      `${process.env.REACT_APP_API_URL}/dids/checkAvailability?dids=${dids}`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting DIDs availability!' });
  }
});

/** @deprecated */
export const postTemporaryDID = createAsyncThunk<{ success: boolean }, { id: number }, { state: RootReducerState }>(
  'dids/postTemporaryDID',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.post<{
        success: boolean;
      }>(`${process.env.REACT_APP_API_URL}/dids/${id}/temporary`, {});
      thunkAPI.dispatch(getDIDsList(thunkAPI.getState().didsReducer.didsRouteParams));
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating temporary DID!' });
    }
  }
);

export const patchDIDCnam = createAsyncThunk<
  { success: boolean },
  { id: number; data: { cnam?: string | null } },
  { state: RootReducerState }
>('dids/patchDIDCnam', async ({ id, data }, thunkAPI) => {
  try {
    const response = await axios.patch<{
      success: boolean;
    }>(`${process.env.REACT_APP_API_URL}/dids/${id}/cnam`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during changing DID!' });
  }
});

export const patchDIDE911 = createAsyncThunk<
  { success: boolean },
  { id: number; data: AddressType },
  { state: RootReducerState }
>('dids/patchDIDE911', async ({ id, data }, thunkAPI) => {
  try {
    if (data.zipCode) delete data.zipCode;

    const response = await axios.patch<{
      success: boolean;
    }>(`${process.env.REACT_APP_API_URL}/dids/${id}/addresse911`, data);
    thunkAPI.dispatch(getProvisionDIDE911({ id, isCustomAddress: true }));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during Overwritten E911!' });
  }
});

export const getVerifyDIDE911 = createAsyncThunk<{ success: boolean }, { id: number }, { state: RootReducerState }>(
  'dids/getVerifyDIDE911',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.get<{
        success: boolean;
      }>(`${process.env.REACT_APP_API_URL}/dids/${id}/verify-e911`);
      thunkAPI.dispatch(getDIDDetails({ id }));
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during Provision E911!' });
    }
  }
);

export const getProvisionDIDE911 = createAsyncThunk<
  { success: boolean },
  { id: number; isCustomAddress: boolean },
  { state: RootReducerState }
>('dids/getProvisionDIDE911', async ({ id, isCustomAddress = false }, thunkAPI) => {
  try {
    const query = isCustomAddress ? '?custom_address=true' : '';
    const url = `${process.env.REACT_APP_API_URL}/dids/${id}/provision-e911${query}`;

    const response = await axios.get<{ success: boolean }>(url);
    thunkAPI.dispatch(getDIDE911({ id }));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during Provision E911!' });
  }
});

export const getDIDDNIS = createAsyncThunk<
  CSRPConditionaTranslationPrefixRuleType | null,
  { id: number },
  { state: RootReducerState }
>('dids/getDIDDNIS/no-loader', async ({ id }, thunkAPI) => {
  try {
    const url = `${process.env.REACT_APP_API_URL}/dids/${id}/dnis`;

    const response = await axios.get<CSRPConditionaTranslationPrefixRuleType | null>(url);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting DNIS Settings!' });
  }
});

export const getDIDCSRP = createAsyncThunk<CSRPInformation, { id: number }, { state: RootReducerState }>(
  'dids/getDIDCSRP/no-loader',
  async ({ id }, thunkAPI) => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/dids/${id}/csrp`;

      const response = await axios.get<CSRPInformation>(url);
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting CSRP Information!' });
    }
  }
);

export const getDIDE911 = createAsyncThunk<DIDE911Type, { id: number }, { state: RootReducerState }>(
  'dids/getDIDE911/no-loader',
  async ({ id }, thunkAPI) => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/dids/${id}/e911`;

      const response = await axios.get<DIDE911Type>(url);
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting E911!' });
    }
  }
);

export const postDIDDNIs = createAsyncThunk<
  { success: boolean },
  { id: number; dnis: string; enabled: boolean; dnisId?: number },
  { state: RootReducerState }
>('dids/postDIDDNIs', async ({ id, enabled, dnis, dnisId }, thunkAPI) => {
  try {
    const response = await axios.post<{ success: boolean }>(`${process.env.REACT_APP_API_URL}/dids/${id}/dnis`, {
      enabled,
      dnis,
      dnisId,
    });
    thunkAPI.dispatch(getDIDDetails({ id }));
    thunkAPI.dispatch(getDIDDNIS({ id }));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating did DNIS!' });
  }
});

export const getDIDRCF = createAsyncThunk<CSRPCosDestinationType | null, { id: number }, { state: RootReducerState }>(
  'dids/getDIDRCF/no-loader',
  async ({ id }, thunkAPI) => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/dids/${id}/rcf`;

      const response = await axios.get<CSRPCosDestinationType | null>(url);
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting RCF Settings!' });
    }
  }
);

export const postDIDRCF = createAsyncThunk<
  { success: boolean },
  { id: number; rcf: string; enabled: boolean },
  { state: RootReducerState }
>('dids/postDIDRCF', async ({ id, enabled, rcf }, thunkAPI) => {
  try {
    const response = await axios.post<{ success: boolean }>(`${process.env.REACT_APP_API_URL}/dids/${id}/rcf`, {
      enabled,
      rcf,
    });
    thunkAPI.dispatch(getDIDRCF({ id }));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating did RCF!' });
  }
});

export const fixDIDRouting = createAsyncThunk<void, { id: number }, { state: RootReducerState }>(
  'dids/fixDIDRouting',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.post<void>(`${process.env.REACT_APP_API_URL}/dids/${id}/fix-routing`);
      thunkAPI.dispatch(getDIDRouting({ id }));

      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fixing did routing!' });
    }
  }
);

export const postPBXExtensionDID = createAsyncThunk<
  { success: boolean },
  {
    id: number;
    data: {
      pbx_extension?: { id: number };
      pbx_extension_type?: { id: number };
      disconnect?: boolean;
    };
  },
  { state: RootReducerState }
>('dids/postPBXExtensionDID', async ({ id, data }, thunkAPI) => {
  try {
    let url = `${process.env.REACT_APP_API_URL}/dids/${id}/pbxExtension`;

    if (data.pbx_extension) {
      url = `${process.env.REACT_APP_API_URL}/dids/${id}/pbxExtension`;
    }

    if (data.pbx_extension_type) {
      url = `${process.env.REACT_APP_API_URL}/dids/${id}/createExtension`;
    }

    if (data.disconnect) {
      url = `${process.env.REACT_APP_API_URL}/dids/${id}/disconnectPbxExtension`;
    }

    const response = await axios.post<{ success: boolean }>(url, data);
    thunkAPI.dispatch(getDIDDetails({ id }));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during assigning DID to PBX Extension!' });
  }
});
